import React, { HTMLAttributes } from "react";
import { BadgeProps, TooltipProps } from "@mui/material";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { IconComponent } from "../..";
import styles from "./CustomIconButton.module.css";
import { CustomTooltip } from "../../tooltip";
import { CustomBadge } from "../../badges";
import { getFilteredDataAttributes } from "../../../utils";

type IconSize = React.ComponentProps<typeof IconComponent>["size"];
const iconSizes: Record<ButtonSize, IconSize> = {
	xs: "xs",
	sm: "sm",
	md: "md",
	lg: "md",
};
type SvgIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;
type ButtonSize = "xs" | "sm" | "md" | "lg";
type ButtonVariant = "contained" | "text" | "outlined" | "full-outlined";
type ButtonColor = "secondary" | "primary";
type IconSource = IconProp | SvgIconComponent;
interface CustomIconButtonProps extends HTMLAttributes<HTMLSpanElement> {
	"data-testid"?: string | null;
	badgeClassName?: string;
	badgeColor?: BadgeProps["color"];
	badgeContent?: BadgeProps["badgeContent"];
	btnClassName?: string;
	Color?: ButtonColor;
	disableHovering?: boolean;
	disabled?: boolean;
	icon?: IconSource;
	iconClassName?: string;
	iconColor?: string;
	iconRotation?: FontAwesomeIconProps["rotation"];
	label?: string;
	size?: ButtonSize;
	tooltip?: TooltipProps["title"];
	tooltipPlacement?: TooltipProps["placement"];
	variant?: ButtonVariant;
}
const CustomIconButton = React.forwardRef<HTMLButtonElement, CustomIconButtonProps>(
	(
		{
			"data-testid": dataTestId = null,
			badgeClassName = "",
			badgeColor = "default",
			badgeContent = "",
			btnClassName = "",
			className = "",
			color = "secondary",
			disableHovering = false,
			disabled,
			icon,
			iconClassName = "",
			iconColor = "inherit",
			iconRotation,
			id = "",
			label = "",
			onClick = undefined,
			onMouseDown = undefined,
			onMouseLeave = undefined,
			onMouseOver = undefined,
			onTouchStart = undefined,
			size = "sm",
			tooltip = "",
			tooltipPlacement = "bottom",
			variant = "text",
			...dataAttributes
		},
		ref
	) => {
		const handleClick = disabled ? undefined : onClick;
		const filteredDataAttributes = getFilteredDataAttributes(dataAttributes);
		return (
			<CustomTooltip placement={tooltipPlacement} title={tooltip}>
				<span className={`${styles.wrapper} ${className}`} id={id} {...filteredDataAttributes}>
					<button
						ref={ref}
						className={`${styles.btn} ${btnClassName}`}
						data-color={color}
						data-disabledhovering={disableHovering}
						data-size={size}
						data-testid={dataTestId}
						data-variant={variant}
						disabled={disabled}
						type="button"
						onClick={handleClick}
						onMouseDown={onMouseDown}
						onMouseEnter={onMouseOver}
						onMouseLeave={onMouseLeave}
						onTouchStart={onTouchStart}
						{...filteredDataAttributes}
					>
						<span className={styles.btn__label}>
							{icon
								? (badgeContent && (
										<CustomBadge
											className={`${styles.btn__badge} ${badgeClassName}`}
											color={badgeColor}
											content={badgeContent}
										>
											<span>
												<IconComponent
													className={`${styles.btn__icon} ${iconClassName}`}
													color={iconColor}
													icon={icon}
													rotation={iconRotation}
													size={iconSizes[size]}
												/>
											</span>
										</CustomBadge>
								  )) || (
										<IconComponent
											className={`${styles.btn__icon} ${iconClassName}`}
											color={iconColor}
											icon={icon}
											rotation={iconRotation}
											size={iconSizes[size]}
										/>
								  )
								: label}
						</span>
					</button>
				</span>
			</CustomTooltip>
		);
	}
);

export default CustomIconButton;
